'use client';

/**
 * UserStreamListener.tsx — lytter på SSE-events fra user-stream
 *
 * Pluggger seg på user-stream-handlers og viser Toast når events mottas.
 * Dispatcher også et custom DOM-event ('user-stream:chat_message') slik at
 * spesifikke sider (f.eks. JobChat) kan reagere uten at user-stream kobles
 * direkte til dem.
 *
 * Rendres som null — ingen UI, kun side-effect.
 *
 * Plassering: inne i AuthProvider (slik at den kun er aktiv når brukeren
 * er autentisert). Strømmen åpnes/lukkes av AuthContext.
 */

import { useEffect } from 'react';
import { addUserStreamListener, removeUserStreamListener } from '@/lib/user-stream';
import {
  addNotification,
  mapEventToNotification,
  setNotificationUserId,
} from '@/lib/notifications';
import { getCurrentUser } from '@/lib/auth';
import { useToast } from '@/components/data';

/**
 * Custom DOM-event-navn som sider kan lytte på. Dispatches på window.
 * Andre sider (JobChat) lytter på disse for å re-hente data.
 */
export const USER_STREAM_EVENT_CHAT_MESSAGE = 'user-stream:chat_message';

interface ChatMessageEventData {
  chat_id: number;
  message: {
    message_id: number;
    tenant_id: string;
    [key: string]: unknown;
  };
}

export default function UserStreamListener() {
  // NB: showToast er FUNKSJONEN — toast er ReactNode (portal-noden).
  // UserStreamListener rendrer null og trenger kun funksjonen.
  const { showToast } = useToast();

  useEffect(() => {
    // Gi store innlogget bruker-ID — brukes til å filtrere bort brukerens
    // EGEN events (ingen notifikasjon/lyd på det man selv sendte).
    setNotificationUserId(getCurrentUser()?.id ?? null);

    const handler = {
      onEvent: (eventType: string, data: unknown) => {
        // 1. ALLE events går gjennom notifikasjonsstore (plan 2026-09-23):
        //    event-TYPE avgjør notifikasjonstype. Store spiller
        //    Notification.mp3 ved mottakelse. Egne events filtreres bort.
        addNotification(mapEventToNotification(eventType, data));

        // 2. Chat-spesifikk toast (eksisterende atferd). playSound: false —
        //    Notification.mp3 fra store er den ene autoritative lyden,
        //    ellers ville toasten doblet opp med Success.mp3.
        if (eventType === 'chat_message') {
          const chatData = data as ChatMessageEventData;
          // Vis Toast til brukeren
          showToast({
            kind: 'info',
            title: 'Ny melding',
            subtitle: `Chat #${chatData.chat_id}: ny melding mottatt`,
            autoCloseMs: 4000,
            playSound: false,
          });
        }
        // Ukjente event-typer ignoreres her — de er allerede håndtert
        // av notifikasjonsstore over (generisk varsel).
      },
      onError: (error: unknown) => {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[user-stream] SSE error:', error);
        }
        // 401/403 fra SSE = utløpt/revokert JWT. Strømmen er allerede lukket
        // av user-stream.ts — her sender vi brukeren til beskjed-siden.
        if (
          error instanceof Error &&
          error.message.includes('token avvist')
        ) {
          window.location.href = '/session-expired';
        }
      },
      onOpen: () => {
        if (process.env.NODE_ENV === 'development') {
          console.info('[user-stream] SSE-tilkobling åpnet');
        }
      },
    };

    addUserStreamListener(handler);

    // Cleanup: fjern lytter når komponenten unmountes
    return () => {
      removeUserStreamListener(handler);
    };
  }, [showToast]);

  return null;
}